// A price field on the dashboard (plan section 6, Owner screens).
//
// The owner types at a keyboard, so a price is read as it is written: "50"
// is fifty. The text is kept as typed while the field is in use, and tidied
// to the currency's own form ("50.00") when the owner leaves it, so what will
// be saved is on screen before Save. The till keeps its keypad, which fills
// from the right (format/money.ts, parseTypedMinor).

import { formatMinor, parsePrice, type PriceProblem } from '../format/money';
import { MAX_MINOR } from '../domain/pricing';
import { fill, message, type Language, type MessageKey } from '../i18n/messages';

const PROBLEMS: Record<Exclude<PriceProblem, 'empty'>, MessageKey> = {
	invalid: 'priceInvalid',
	decimals: 'priceDecimals',
	negative: 'priceNegative',
	large: 'priceLarge',
};

export class PriceInput {
	text = $state('');
	/** Set once the owner has left the field or tried to save. */
	checked = $state(false);

	private readonly minorDigits: () => number;
	private readonly options: { negative?: boolean; optional?: boolean };

	constructor(minorDigits: () => number, options: { negative?: boolean; optional?: boolean } = {}) {
		this.minorDigits = minorDigits;
		this.options = options;
	}

	/** Start from a stored amount, or from nothing. */
	load(minor: number | null | undefined): void {
		this.text = minor == null ? '' : formatMinor(minor, this.minorDigits());
		this.checked = false;
	}

	read = $derived.by(() =>
		parsePrice(this.text, this.minorDigits(), {
			negative: this.options.negative,
			max: MAX_MINOR,
		}),
	);

	/** The amount in minor units, null for an optional field left empty. */
	get minor(): number | null {
		return this.read.ok ? this.read.minor : null;
	}

	/** Whether the form may save: a number that reads, or an allowed blank. */
	get valid(): boolean {
		return this.read.ok || (this.read.problem === 'empty' && !!this.options.optional);
	}

	set = (value: string): void => {
		this.text = value;
		this.checked = false;
	};

	tidy = (): void => {
		this.checked = true;
		if (this.read.ok) this.text = formatMinor(this.read.minor, this.minorDigits());
	};

	/** What to say under the field, once the owner has left it. */
	error(language: Language): string {
		if (!this.checked || this.valid) return '';
		const problem = this.read.ok ? null : this.read.problem;
		if (problem === null) return '';
		if (problem === 'empty') return message(language, 'priceRequired');
		const text = message(language, PROBLEMS[problem]);
		return problem === 'decimals' ? fill(text, this.minorDigits()) : text;
	}
}
